import {
  clampNumber,
  ExerciseType,
  GeospatialPoint,
  isWholeNumber,
  num,
  PinnedNoteEntry,
  RPE,
  SetType,
  ShareToPlatform,
  WorkoutBiometrics,
  WorkoutMedia,
  WorkoutVisibility,
} from '..';

/**
 * Events are used to determine the start time, end time and duration of a
 * `NormalizedWorkout`, in a way that can be persisted to disk.
 */
export interface NormalizedWorkoutEvent {
  type: 'resume' | 'pause';
  unixTimestamp: number;
}

/**
 * NormalizedWorkout describes the state of a workout. It's used when
 * persisting an ongoing workout to disk, or calculating shareables after
 * completing the workout.
 *
 * The duration of a `NormalizedWorkout` is _derived_ from the `timeEvents`
 * array.  There is no single "duration" variable, because that would require
 * pausing to be a complex stateful operation.
 */
export interface NormalizedWorkout {
  name: string;
  description: string;
  media: WorkoutMedia[];
  startTime: number;
  timeEvents: NormalizedWorkoutEvent[];
  exercises: NormalizedExercise[];
  routineId?: string;
  trackWorkoutAsRoutine: boolean;
  updateRoutineFromWorkout?: boolean;
  appleWatch: boolean;
  wearosWatch: boolean;
  workoutVisibility: WorkoutVisibility;
  isWorkoutBiometricsPublic: boolean;
  isShownOnGymProfile: boolean;
  shareTo: {
    [key in ShareToPlatform]: boolean;
  };
  clientId: string;
  biometrics?: WorkoutBiometrics;
  trainerProgramId: string | undefined;
  trainerWorkoutTemplateId: string | undefined;
  gymId: string | undefined;
  isHomeGym: boolean;
}

export interface NormalizedSet {
  index: number;
  indicator: SetType;
  weight?: number;
  reps?: number;
  distance?: number;
  duration?: number;
  customMetric?: number;
  rpe?: RPE;
  targetRpe?: RPE;
  completed: boolean;
  completedAt?: string;
  geospatialData?: GeospatialPoint[];
}

export interface NormalizedPinnedNote extends PinnedNoteEntry {
  type: 'routine-exercise' | 'workout-exercise';
}

export interface NormalizedExercise {
  title: string;
  exerciseTemplateId: string;
  supersetId?: number;
  autoRestTimerSeconds?: number;
  /** @deprecated use pinned notes instead */
  routineNotes: string;
  /** @deprecated use pinned notes instead */
  notes: string;
  volumeDoublingEnabled: boolean;
  sets: NormalizedSet[];
  pinned_notes: NormalizedPinnedNote[];
}

export const calculateDuration = (
  normalizedWorkout: Pick<NormalizedWorkout, 'startTime' | 'timeEvents'>,
  nowUnix: number,
) => {
  const { startTime, timeEvents } = normalizedWorkout;

  if (!timeEvents.length) return nowUnix - startTime;

  const timeBeforeFirstEvent = timeEvents[0].unixTimestamp - startTime;

  const duration = timeEvents.reduce((prev, curr, index, array) => {
    const nextEvent = array[index + 1] ?? {
      type: 'resume',
      unixTimestamp: nowUnix,
    };

    if (curr.type === 'pause') {
      return prev;
    } else {
      return prev + nextEvent.unixTimestamp - curr.unixTimestamp;
    }
  }, timeBeforeFirstEvent);

  return duration;
};

export const calculateEndTime = (
  normalizedWorkout: Pick<NormalizedWorkout, 'startTime' | 'timeEvents'>,
  nowUnix: number,
) => {
  const { startTime } = normalizedWorkout;
  const durationSeconds = calculateDuration(normalizedWorkout, nowUnix);

  return startTime + durationSeconds;
};

interface GenerateWorkoutTimeEventsArgs {
  startTimeUnix: number;
  durationSeconds: number;
  nowUnix: number;
  /**
   * if this is `true`, generate a pause event at the current timestamp
   */
  isPaused?: boolean;
}

export const generateWorkoutTimeEvents = ({
  startTimeUnix,
  durationSeconds,
  nowUnix,
  isPaused = false,
}: GenerateWorkoutTimeEventsArgs): NormalizedWorkoutEvent[] => {
  const maxPossibleDuration = nowUnix - startTimeUnix;
  const safeRequestedDuration = clampNumber(durationSeconds, {
    min: 0,
    max: maxPossibleDuration,
  });
  const pausePaddingSeconds = maxPossibleDuration - safeRequestedDuration;

  const timeEvents = [];

  if (pausePaddingSeconds > 0) {
    /**
     * The duration is shorter than (endTime - startTime). Add a pause event at
     * the start of the workout for padding, and a resume event at the correct
     * time to make the duration match.
     */
    timeEvents.push(
      ...([
        { type: 'pause', unixTimestamp: startTimeUnix },
        { type: 'resume', unixTimestamp: startTimeUnix + pausePaddingSeconds },
      ] as NormalizedWorkoutEvent[]),
    );
  }

  if (isPaused) {
    timeEvents.push({
      type: 'pause',
      unixTimestamp: startTimeUnix + maxPossibleDuration,
    } as NormalizedWorkoutEvent);
  }

  return timeEvents;
};

export const isWorkoutCurrentlyPaused = (normalizedWorkout: NormalizedWorkout): boolean => {
  return normalizedWorkout.timeEvents.slice(-1)[0]?.type === 'pause';
};

/**
 * When logging a workout, we can't just use the `completed` state of a set to
 * determine if the user meant for the set to be included in the workout. This
 * is because a lot of users never mark sets as completed, and forcing them to
 * do that wouldn't add anything to the app. For that reason, we count a set as
 * completed if it contains inputted valid values based on its exercise type.
 * This function filters out all sets in a `NormalizedWorkout` that are
 * incomplete based on the values inputted.
 */
export const trimWorkout = (
  workout: NormalizedWorkout,
  exerciseTypeMap: { [key: string]: ExerciseType },
): NormalizedWorkout => {
  // Filter out empty sets.
  const filteredSetsWorkout: NormalizedWorkout = {
    ...workout,
    exercises: workout.exercises.map(e => {
      return {
        ...e,
        sets: e.sets.filter(s => {
          const exerciseType = exerciseTypeMap[e.exerciseTemplateId];

          if (!exerciseType) return true;

          return isLoggedSet(s, exerciseType);
        }),
      };
    }),
  };

  // Filter out exercises that contain 0 sets.
  const filteredWorkout = {
    ...filteredSetsWorkout,
    exercises: filteredSetsWorkout.exercises.filter(e => !!e.sets.length),
  };

  return filteredWorkout;
};

export const isLoggedSet = (set: NormalizedSet, exerciseType: ExerciseType): boolean =>
  set.completed || !isEmptySet(set, exerciseType);

export const isInvalidSet = (set: NormalizedSet, exerciseType?: ExerciseType): boolean => {
  if (!exerciseType) return false;

  switch (exerciseType) {
    case 'weight_reps':
    case 'bodyweight_reps':
    case 'bodyweight_assisted_reps':
      return isMissingReps(set, exerciseType) || isInvalidWeightSet(set);

    case 'reps_only':
      return isMissingReps(set, exerciseType) || isInvalidRepsSet(set);

    case 'distance_duration':
      return isInvalidDistanceDurationSet(set);

    case 'duration':
      return isInvalidDurationSet(set);
  }

  return false;
};

const isMissingReps = (set: NormalizedSet, exerciseType: ExerciseType) =>
  !set.reps && set.indicator !== 'failure' && isLoggedSet(set, exerciseType);

const isInvalidWeightSet = (set: NormalizedSet) => {
  if (set.distance || set.duration) return true;

  if (num(set.weight) > 99999) return true;

  if (num(set.reps) > 99999) return true;

  if (set.reps && !isWholeNumber(set.reps)) return true;

  return false;
};

const isInvalidRepsSet = (set: NormalizedSet) => {
  if (set.distance || set.duration || set.weight) return true;

  if (num(set.reps) > 99999) return true;

  if (set.reps && !isWholeNumber(set.reps)) return true;

  return false;
};

const isInvalidDistanceDurationSet = (set: NormalizedSet) => {
  if (set.reps || set.weight) return true;

  if (set.distance && !isWholeNumber(set.distance)) return true;

  if (set.duration && !isWholeNumber(set.duration)) return true;

  return false;
};

const isInvalidDurationSet = (set: NormalizedSet) => {
  if (set.reps || set.weight || set.distance) return true;

  if (set.duration && !isWholeNumber(set.duration)) return true;

  return false;
};

const isEmptySet = (set: NormalizedSet, exerciseType: ExerciseType): boolean => {
  switch (exerciseType) {
    case 'weight_reps':
    case 'bodyweight_reps':
    case 'bodyweight_assisted_reps':
    case 'reps_only':
      return !set.reps && !set.weight;
    case 'distance_duration':
    case 'duration':
      return !set.duration && !set.distance;
    case 'short_distance_weight':
      return !set.distance && !set.weight;
    case 'weight_duration':
      return !set.weight && !set.duration;
    case 'floors_duration':
    case 'steps_duration':
      return !set.customMetric && !set.duration;
  }
};
